import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useHistory, Redirect } from 'react-router-dom';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../graphql/iot-documents';
import * as s from './styled-variaveis';
import {
  Breadcumb,
  Overview,
  AplicacaoInfo,
  ThingInfo,
  VariaveisFavoritas,
  FiltraVariaveis,
  SetPoint,
  SupervisorioList,
  SetPointsList,
} from './content';
import { icons } from '../../assets';
import {
  getAllVariablesAmount,
  getVariableGroups,
  getAllSetPoints,
} from '../../services';
import {
  getStatus,
  notifyError,
  createReconnectingSubscription,
  sessionCache,
} from '../../utils';

const groups = [
  {
    label: 'Medições de Campo',
    value: 'supervisorio',
  },
  {
    label: 'Alarme',
    value: 'alarme',
  },
  {
    label: 'Operação',
    value: 'operacao',
  },
  {
    label: 'Parâmetros',
    value: 'parametros',
  },
];

const TodasVariaveis = () => {
  const [selectedTab, setSelectedTab] = useState('supervisorio');
  const [totalVariables, setTotalVariables] = useState(0);
  const [loading, setLoading] = useState(true);
  const [setPointsGroupsList, setSetPointsGroupsList] = useState([]);
  const [setPointsGroupsData, setSetPointsGroupsData] = useState({});
  const [filter, setFilter] = useState('');
  const history = useHistory();
  const dispatch = useDispatch();
  // Guard de refresh: a rota nao tem :id, entao se chegamos sem state
  // (refresh direto, link copiado), nao temos como saber qual thing
  // carregar. Fallback {} previne crash; o Redirect abaixo (apos os
  // hooks) volta pro contexto anterior.
  const { aplicacao, thing } = history.location.state || {};
  // Antes: useSelector(state => state) puxava o store inteiro,
  // causando re-render desta tela a cada dispatch do app (alarmes,
  // subscriptions de OUTRAS things, navegacao, etc). Agora seleciona
  // so a entrada do iot para a thing atual + shallowEqual pra evitar
  // re-render quando o objeto interno nao mudou de fato (a entry tem
  // { values, variables, unsubscribe } e setters tipicos remontam
  // o objeto com mesmas refs).
  const iotEntry = useSelector(
    (state) => (thing && state.iot ? state.iot[thing.thing] : null),
    shallowEqual
  );
  const customer = useSelector((state) => state.parque.customer.selectedPark);

  useEffect(() => {
    if (!thing || !thing.id) return undefined;
    // sessionCache: getVariableGroups e getAllVariablesAmount sao
    // imutaveis na sessao por thingId — segunda visita a mesma usina
    // ja encontra cache hit em memoria e pula HTTPs.
    sessionCache('variableGroups', thing.id, () => getVariableGroups(thing.id))
      .then(({ data }) => {
        setSetPointsGroupsList(
          data.filter(({ group }) => group !== 'supervisorio')
        );

        initConnection(
          data.filter(({ group }) => group === 'supervisorio').pop().id
        );
        setSetPointsGroupsList(data);
      })
      .catch(() => {
        notifyError('Erro ao buscar os Set Points');
      });

    sessionCache('variablesAmount', thing.id, () =>
      getAllVariablesAmount(thing.id)
    )
      .then((resp) => {
        setTotalVariables(resp.data.amount);
      })
      .catch(() => {
        notifyError('Não foi possível buscar a quantidade de variáveis');
      });

    // Subscription com reconnect automatico: se a conexao cair (queda
    // de wifi, AppSync reinicia, token expira), o helper reconecta com
    // backoff exponencial. Antes a subscription so chamava unsubscribe no
    // erro, deixando os dados congelados na tela sem aviso.
    let mounted = true;
    let activeRecon = null;

    const initConnection = async (typeId) => {
      // Variaveis CADASTRADAS (independem de telemetria) — carrega sempre,
      // pra a lista aparecer mesmo sem conexao/dados ao vivo.
      dispatch({
        type: 'IOT/SAGA_GET_THING_VARIABLES',
        thingId: thing.id,
        thingName: thing.thing,
        typeId,
      });
      try {
        const [iotQuery, updateIotSubscription] = await Promise.all([
          getIOTDocument(),
          onUpdateIOTDocument(),
        ]);
        const response = await API.graphql(
          graphqlOperation(iotQuery, { thingName: thing.thing })
        );
        const iotValues =
          response.getIOT || (response.data && response.data.getIOT);

        if (!iotValues) {
          // Sem telemetria: mesmo assim mandamos valores VAZIOS pra a lista
          // renderizar as variaveis cadastradas (valor '—' em cada uma).
          notifyError('Não foi possível se conectar com a Thing');
          if (mounted) {
            dispatch({
              type: 'IOT/SET_VALUES_THING',
              values: {},
              thingName: thing.thing,
            });
            setLoading(false);
          }
          return;
        }
        if (!mounted) return;

        activeRecon = createReconnectingSubscription({
          label: `all-variables/${thing.thing}`,
          buildSubscription: ({ next, error }) =>
            API.graphql(
              graphqlOperation(updateIotSubscription, {
                thingName: thing.thing,
              })
            ).subscribe({ next, error }),
          onNext: (subscribeData) => {
            const update =
              subscribeData.value &&
              subscribeData.value.data &&
              subscribeData.value.data.onUpdateIOT;
            if (!update || !mounted) return;
            dispatch({
              type: 'IOT/SET_VALUES_THING',
              values: update,
              thingName: thing.thing,
              unsubscribe: () => activeRecon && activeRecon.unsubscribe(),
            });
            setLoading(false);
          },
        });

        // Dispatch inicial com o snapshot do getIOT (antes da primeira
        // mensagem da subscription)
        dispatch({
          type: 'IOT/SET_VALUES_THING',
          values: iotValues,
          thingName: thing.thing,
          unsubscribe: () => activeRecon && activeRecon.unsubscribe(),
        });

        setLoading(false);
      } catch {
        // Falha de conexao: valores vazios pra a lista ainda mostrar as
        // variaveis cadastradas (sem valor ao vivo).
        notifyError('Não foi possível se conectar com a Thing');
        if (mounted) {
          dispatch({
            type: 'IOT/SET_VALUES_THING',
            values: {},
            thingName: thing.thing,
          });
          setLoading(false);
        }
      }
    };

    return () => {
      mounted = false;
      if (activeRecon && typeof activeRecon.unsubscribe === 'function') {
        activeRecon.unsubscribe();
      }
    };
    // Mount-only: abre subscriptions e busca grupos ao entrar na tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!thing || !thing.id) return;
    if (setPointsGroupsList.length) {
      let newGroups = {};
      setPointsGroupsList.forEach(({ id, group }) => {
        getAllSetPoints(thing.id, id)
          .then((resp) => {
            newGroups = {
              ...newGroups,
              [group]: resp.data,
            };
            if (
              Object.entries(newGroups).length === setPointsGroupsList.length
            ) {
              setSetPointsGroupsData(newGroups);
            }
          })
          .catch(() => {
            notifyError('Não foi possível buscar a quantidade de variáveis');
          });
      });
    }
    // thing pode ser undefined em refresh sem state; effect ja faz
    // early return nesse caso. setPointsGroupsList e a dep relevante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPointsGroupsList]);

  // Apos todos os hooks: se nao temos contexto de navegacao, volta uma
  // rota. JSX abaixo acessa thing.thing, aplicacao.id, etc — sem state
  // estourava TypeError em refresh direto.
  if (!history.location.state) {
    return <Redirect to="/dashboard/customer/plant" />;
  }

  return (
    <s.Container>
      <s.Line header>
        <Breadcumb
          paths={[
            { pathName: 'Dashboard', to: '/dashboard' },
            { pathName: 'Cliente', to: '/dashboard/customer' },
            {
              pathName: thing.type
                ? thing.type.id === 1 || thing.type.id === '1'
                  ? 'Ucha'
                  : 'Eba'
                : 'Usina',
              // Antes era goBack:true (history.goBack()) que perdia
              // contexto em refresh / link compartilhado (historia
              // vazia → erro). Agora navega explicitamente carregando
              // o state da aplicacao pra /usina reconstruir contexto.
              to: '/dashboard/customer/plant',
              props: { ...aplicacao, thing: aplicacao.thing },
            },
            {
              pathName: 'aplicação',
            },
          ]}
        />
        <Overview nome={customer.name} status={getStatus(aplicacao.status)} />
        <AplicacaoInfo
          nome={aplicacao.description}
          descricao={aplicacao.model}
          energia={iotEntry && iotEntry.values}
        />
        <ThingInfo
          thing={thing}
          aplicacao={aplicacao}
          variables={iotEntry && iotEntry.variables}
          loading={loading}
          values={iotEntry && iotEntry.values}
        />
      </s.Line>

      <s.TabsAndSearch>
        <s.TabsRow>
          {groups.map(({ label, value }, i) => (
            <SetPoint
              key={value}
              active={selectedTab === value}
              onClick={() => setSelectedTab(value)}
              icon={i === 0 ? icons.Sistemas : icons.EngrenagemIconLight}
              name={label}
              type={i === 0 ? 'Sistema' : 'Set Point'}
              totalVariables={
                i === 0
                  ? totalVariables
                  : setPointsGroupsData[value]
                  ? setPointsGroupsData[value].length
                  : null
              }
            />
          ))}
        </s.TabsRow>
        <FiltraVariaveis setFilter={setFilter} />
      </s.TabsAndSearch>

      <s.Line third>
        <VariaveisFavoritas thingId={thing.id} />
      </s.Line>
      {iotEntry && iotEntry.variables && iotEntry.values && (
        <>
          {selectedTab !== 'supervisorio' ? (
            <SetPointsList
              filter={filter}
              setPoints={setPointsGroupsData[selectedTab]}
              values={iotEntry.values}
              thingId={thing.id}
              thingName={thing.thing}
            />
          ) : (
            <SupervisorioList
              filter={filter}
              thingId={thing.id}
              thingName={thing.thing}
              setPoint={selectedTab !== 'Supervisório'}
              // variables={iot[thing.thing].variables}
              // values={iot[thing.thing].values}
              clearFilter={() => {
                setFilter('');
              }}
            />
          )}
        </>
      )}
    </s.Container>
  );
};

export default TodasVariaveis;
